import type { Mermaid } from 'mermaid';

import type {
  MermaidRenderOptions,
  MermaidRenderRequest,
  MermaidRenderResult,
  MermaidRenderTheme,
  MermaidValidationResult,
} from '../mermaid/renderer/types';

export const MERMAID_MAX_TEXT_SIZE = 50_000;
export const MERMAID_MAX_EDGES = 500;

let mermaidPromise: Promise<Mermaid> | null = null;
let mermaidRenderQueue: Promise<void> = Promise.resolve();

function toTheme(theme: MermaidRenderTheme | undefined) {
  switch (theme) {
    case 'modern':
      return 'base' as const;
    case 'dark':
      return 'dark' as const;
    default:
      return 'default' as const;
  }
}

function createClassicMermaidConfig(options?: MermaidRenderOptions) {
  return {
    startOnLoad: false,
    theme: toTheme(options?.theme),
    securityLevel: 'strict' as const,
    htmlLabels: false,
    maxTextSize: MERMAID_MAX_TEXT_SIZE,
    maxEdges: MERMAID_MAX_EDGES,
    fontFamily: options?.fontFamily ?? 'IBM Plex Mono',
    flowchart: { useMaxWidth: true },
    sequence: { useMaxWidth: true },
    gantt: { useMaxWidth: true },
    pie: { useMaxWidth: true },
    journey: { useMaxWidth: true },
    gitGraph: { useMaxWidth: true },
  };
}

async function loadMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then(module => module.default);
  }
  return mermaidPromise;
}

function createDiagramId() {
  return `mermaid-diagram-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function enqueueClassicMermaidTask<T>(task: () => Promise<T>): Promise<T> {
  const run = mermaidRenderQueue.then(task, task);
  mermaidRenderQueue = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

function boundedErrorMessage(error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  return message.replace(/\s+/g, ' ').trim().slice(0, 400);
}

export async function validateClassicMermaid(
  request: MermaidRenderRequest
): Promise<MermaidValidationResult> {
  return enqueueClassicMermaidTask(async () => {
    const mermaid = await loadMermaid();
    mermaid.initialize(createClassicMermaidConfig(request.options));

    try {
      const result = await mermaid.parse(request.code, {
        suppressErrors: true,
      });
      return result === false
        ? { valid: false, error: 'Invalid Mermaid syntax.' }
        : { valid: true };
    } catch (error) {
      return {
        valid: false,
        error: boundedErrorMessage(error) || 'Invalid Mermaid syntax.',
      };
    }
  });
}

export async function renderClassicMermaidSvg(
  request: MermaidRenderRequest
): Promise<MermaidRenderResult> {
  return enqueueClassicMermaidTask(async () => {
    const mermaid = await loadMermaid();
    mermaid.initialize(createClassicMermaidConfig(request.options));

    const { svg } = await mermaid.render(createDiagramId(), request.code);
    return { svg };
  });
}
